import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';
import DocumentInfoAndFiles from '../../common/DocumentInfoAndFiles';
import YKienMauPopup from '../../common/YKienMauPopup';

interface TrinhPhoGiamDocPopupProps {
  open: boolean;
  onClose: () => void;
  documentId?: string; // ID văn bản đầu tiên (để tương thích ngược)
  documentData?: any; // Có thể là một văn bản hoặc mảng văn bản
  action?: string; // Action từ menu API
  onActionSuccess?: () => void; // Callback khi action thành công
}

interface UserData {
  id: number;
  username: string;
  fullname: string;
  position: string;
  department: string;
  email: string;
  roles: string[];
  is_active: boolean;
  created_at: string;
}

const TrinhPhoGiamDocPopup: React.FC<TrinhPhoGiamDocPopupProps> = ({
  open,
  onClose,
  documentId,
  documentData,
  action,
  onActionSuccess
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedPerson, setSelectedPerson] = useState<UserData | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [yKienMauOpen, setYKienMauOpen] = useState(false);
  const [noteSuggestions, setNoteSuggestions] = useState<string[]>([]);
  const [noteSuggestionsLoading, setNoteSuggestionsLoading] = useState(false);
  /** Lãnh đạo ban xem để biết (không bắt buộc), không trùng người được trình phó giám đốc */
  const [selectedXemDeBiet, setSelectedXemDeBiet] = useState<UserData[]>([]);
  const [lanhDaoBanUsers, setLanhDaoBanUsers] = useState<UserData[]>([]);
  const [lanhDaoBanLoading, setLanhDaoBanLoading] = useState(false);

  // Hàm lấy danh sách note gần đây
  const fetchNoteSuggestions = async () => {
    try {
      setNoteSuggestionsLoading(true);
      const response = await axiosInstance.get('/xlvb/incoming-documents/danhsach-note-top10', {
        params: {
          type: 'incoming_documents.trinh_phogiamdoc'
        }
      });
      
      if (response.data.code === 0 && response.data.data?.notes) {
        const notes = response.data.data.notes.map((item: any) => item.note);
        setNoteSuggestions(notes);
      } else {
        setNoteSuggestions([]);
      }
    } catch (error) {

      setNoteSuggestions([]);
    } finally {
      setNoteSuggestionsLoading(false);
    }
  };

  // Gọi API lấy danh sách user và note suggestions khi popup mở
  const fetchLanhDaoBanUsers = async () => {
    setLanhDaoBanLoading(true);
    try {
      const response = await axiosInstance.get('qlvb/incoming-documents/users-lanhdao-ban');
      if (response.data.code === 0) {
        setLanhDaoBanUsers(response.data.data?.users || []);
      } else {
        setLanhDaoBanUsers([]);
      }
    } catch {
      setLanhDaoBanUsers([]);
    } finally {
      setLanhDaoBanLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchLanhDaoBanUsers();
      fetchNoteSuggestions();
    }
  }, [open]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await axiosInstance.get('qlvb/incoming-documents/users-trinh-pho-giamdoc');
      
      if (response.data.code === 0) {
        const usersList = response.data.data.users || [];
        setUsers(usersList);
        
        // Tự động chọn user nếu danh sách chỉ có 1 bản ghi
        if (usersList.length === 1) {
          setSelectedPerson(usersList[0]);
        }
      } else {
        const errorMsg = response.data.message || 'Có lỗi xảy ra khi tải danh sách người dùng';
        setUsersError(errorMsg);
        enqueueSnackbar(errorMsg, {
          variant: 'error',
          autoHideDuration: 4000
        });
      }
    } catch (error) {

      const errorMsg = 'Không thể tải danh sách người dùng';
      setUsersError(errorMsg);
      enqueueSnackbar(errorMsg, {
        variant: 'error',
        autoHideDuration: 4000
      });
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPerson) {
      enqueueSnackbar('Vui lòng chọn phó giám đốc', { variant: 'warning' });
      return;
    }

    // Kiểm tra có dữ liệu văn bản không
    if (!documentData || (Array.isArray(documentData) && documentData.length === 0)) {
      enqueueSnackbar('Không tìm thấy văn bản để trình', { variant: 'error' });
      return;
    }

    if (!action) {
      enqueueSnackbar('Chưa có api', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      // Chuẩn bị dữ liệu gửi lên API
      const xemDeBietIds = selectedXemDeBiet
        .filter((u) => u.id !== selectedPerson.id)
        .map((u) => u.id);

      let requestData: any = {
        user_pho_giamdoc_id: selectedPerson.id,
        note: note.trim() || null,
        ...(xemDeBietIds.length > 0 ? { xem_de_biet_user_ids: xemDeBietIds } : {})
      };
      
      if (Array.isArray(documentData)) {
        // Nhiều văn bản - sử dụng doc_ids
        const docIds = documentData.map(doc => doc.id).filter(id => id != null);
        if (docIds.length === 0) {
          enqueueSnackbar('Không tìm thấy ID văn bản hợp lệ', { variant: 'error' });
          return;
        }
        
        requestData.doc_ids = docIds;
      } else {
        // Một văn bản - sử dụng doc_id
        requestData.doc_id = documentData.id || parseInt(documentId || '0');
      }

      const response = await axiosInstance.post(action, requestData);

      if (response.data.code === 0) {
        // Luôn hiển thị thông báo từ backend
        enqueueSnackbar(response.data.message || 'Trình phó giám đốc thành công', { variant: 'success' });
        
        // Nếu có nhiều văn bản và có lỗi, hiển thị thêm chi tiết từng lỗi
        if (Array.isArray(documentData) && response.data.data && response.data.data.results) {
          const results = response.data.data.results;
          const failedResults = results.filter((result: any) => result.status === 'error');
          
          if (failedResults.length > 0) {
            // Hiển thị từng lỗi của từng văn bản
            failedResults.forEach((result: any) => {
              enqueueSnackbar(`ID ${result.doc_id}: ${result.message}`, { 
                variant: 'error',
                autoHideDuration: 4000
              });
            });
          }
        }
        
        onActionSuccess?.(); // Gọi callback để refresh danh sách và đóng block thông tin
        handleClose();
      } else {
        // Lỗi chung hoặc trường hợp đơn lẻ
        enqueueSnackbar(response.data.message || 'Có lỗi xảy ra', { variant: 'error' });
      }
    } catch (error: any) {

      // Xử lý lỗi chi tiết nếu response có cấu trúc batch
      if (error.response?.data?.data?.results) {
        const results = error.response.data.data.results;
        const failedResults = results.filter((result: any) => result.status === 'error');
        
        if (failedResults.length > 0) {
          // Hiển thị từng lỗi của từng văn bản
          failedResults.forEach((result: any) => {
            enqueueSnackbar(`ID ${result.doc_id}: ${result.message}`, { 
              variant: 'error',
              autoHideDuration: 4000
            });
          });
          
          // Nếu có văn bản thành công, hiển thị thông báo tổng hợp
          const successfulCount = results.filter((result: any) => result.status === 'success').length;
          if (successfulCount > 0) {
            enqueueSnackbar(`Thành công ${successfulCount}/${results.length} văn bản`, { 
              variant: 'info',
              autoHideDuration: 3000
            });
          }
        }
      } else {
        // Lỗi chung
        const errorMessage = error.response?.data?.message || 'Có lỗi xảy ra khi trình phó giám đốc';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPerson(null);
    setSelectedXemDeBiet([]);
    setSearchTerm('');
    setNote('');
    setUsers([]);
    setUsersError(null);
    onClose();
  };

  const xemDeBietOptions = lanhDaoBanUsers.filter((u) => u.id !== selectedPerson?.id);

  // Handle YKienMau popup
  const handleOpenYKienMau = () => {
    setYKienMauOpen(true);
  };

  const handleCloseYKienMau = () => {
    setYKienMauOpen(false);
  };

  const handleTemplateSelect = (templateContent: string) => {
    setNote(templateContent);
    setYKienMauOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#086428',
        color: 'white',
        fontWeight: 500,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '51px',
        padding: '12px 16px',
        boxSizing: 'border-box'
      }}>
        Trình phó giám đốc
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {documentData ? (
          <Box sx={{ display: 'flex', minHeight: '400px' }}>
            {/* Bên trái - DocumentInfoAndFiles + Form chọn phó giám đốc */}
            <Box sx={{ 
              flex: 1, 
              pr: 2, 
              borderRight: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {/* DocumentInfoAndFiles - Chỉ hiển thị thông tin văn bản */}
              <Box>
                <DocumentInfoAndFiles 
                  documentData={documentData}
                  showPreview={false}
                  showOnlyInfo={true}
                />
              </Box>

              {/* Chọn Phó giám đốc */}
              <Box>                
                {usersLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Đang tải danh sách Phó giám đốc...</Typography>
                  </Box>
                ) : usersError ? (
                  <Typography variant="body2" color="error" sx={{ p: 2 }}>
                    {usersError}
                  </Typography>
                ) : (
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => option.fullname}
                    value={selectedPerson}
                    onChange={(_, newValue) => {
                      setSelectedPerson(newValue);
                      if (newValue) {
                        setSelectedXemDeBiet((prev) => prev.filter((u) => u.id !== newValue.id));
                      }
                    }}
                    inputValue={searchTerm}
                    onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Lãnh đạo xử lý chính"
                        placeholder="Tìm kiếm theo tên hoặc email..."
                        variant="outlined"
                        size="small"
                        required
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 'auto',
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                            {option.fullname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.position} - {option.department}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    noOptionsText="Không tìm thấy Phó giám đốc"
                  />
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
                  Lãnh đạo ban xem để biết (không bắt buộc)
                </Typography>
                {lanhDaoBanLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Đang tải danh sách lãnh đạo ban...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    multiple
                    options={xemDeBietOptions}
                    value={selectedXemDeBiet}
                    onChange={(_, newValue) => setSelectedXemDeBiet(newValue)}
                    getOptionLabel={(option) => option.fullname}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Chọn thêm (xem để biết)"
                        placeholder="Tìm theo tên..."
                        variant="outlined"
                        size="small"
                        sx={{
                          '& .MuiInputBase-root': {
                            minHeight: 42
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                            {option.fullname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.position} — {option.department}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    noOptionsText="Không có lãnh đạo ban khác để chọn"
                  />
                )}

                {/* Ghi chú */}
                <Autocomplete
                  freeSolo
                  options={noteSuggestions}
                  value={note}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setNote(newValue || '');
                    }
                  }}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setNote(newValue);
                    } else if (newValue === null) {
                      setNote('');
                    }
                  }}
                  loading={noteSuggestionsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ghi chú"
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={5}
                      placeholder="Nhập ghi chú khi trình phó giám đốc..."
                      sx={{
                        mt: 2,
                        '& .MuiInputBase-root': {
                          height: 'auto',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {noteSuggestionsLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option}>
                      <Typography variant="body2" sx={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                        {option}
                      </Typography>
                    </Box>
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                    },
                  }}
                />
                {/* Nút Mẫu nhập */}
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}> 
                <Button
                  size="small"
                  variant="text"
                  onClick={handleOpenYKienMau}
                  sx={{
                    fontSize: '12px',
                    textTransform: 'none',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: '#f5f5f5'
                    },
                    textDecoration: 'underline',
                    mt: 1,
                    alignSelf: 'flex-start'
                  }}
                >
                  (Mẫu nhập)
                </Button>
                </Box>
              </Box>
            </Box>

            {/* Bên phải - Chỉ hiển thị file đính kèm từ DocumentInfoAndFiles */}
            <Box sx={{ flex: 1, pl: 2 }}>
              <DocumentInfoAndFiles 
                documentData={documentData}
                showPreview={true}
                showOnlyFiles={true}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 4,
            minHeight: '400px'
          }}>
            <Typography color="text.secondary">
              Không có thông tin văn bản
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1
      }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            fontSize: '13px',
            borderColor: '#ccc',
            color: '#666',
            '&:hover': {
              borderColor: '#999',
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedPerson}
          sx={{
            fontSize: '13px',
            backgroundColor: '#086428',
            '&:hover': {
              backgroundColor: '#064e1a'
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#999'
            }
          }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Xác nhận'}
        </Button>
      </DialogActions>

      {/* YKienMau Popup */}
      <YKienMauPopup
        open={yKienMauOpen}
        onClose={handleCloseYKienMau}
        onTemplateSelect={handleTemplateSelect}
      />
    </Dialog>
  );
};

export default TrinhPhoGiamDocPopup;